import { Modal, ModalProps, notification } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  Core_SimpleHealthInsuranceType,
  Patientportal_PortalUserMsg,
  useUpdatePortalUserMutation,
} from '@/generated/graphql';
import { InsuranceInfoForm } from '@components/insurance-info-form';
import { Maybe } from 'graphql/jsutils/Maybe';
import { buildPortalUserUpdateInput } from '../../helpers/portal-user';
import { AveliosButton } from '../common-layout/avelios-button';

interface ChangeInsuranceInfoModalProps extends ModalProps {
  currentUser: Maybe<Patientportal_PortalUserMsg>;
  refetchCurrentUser: () => void;
  onClose: () => void;
}

export const ChangeInsuranceInfoModal: FC<ChangeInsuranceInfoModalProps> = ({
  currentUser,
  refetchCurrentUser,
  onClose,
  ...props
}) => {
  const { formatMessage } = useIntl();

  const [insuranceInfo, setInsuranceInfo] = useState<{
    insuranceType: Core_SimpleHealthInsuranceType | null;
    insuranceNumber: string;
    insurerNumber: string;
  }>({ insuranceType: null, insuranceNumber: '', insurerNumber: '' });

  useEffect(() => {
    setInsuranceInfo({
      insuranceType: currentUser?.insuranceInfo?.insuranceType ?? null,
      insuranceNumber: currentUser?.insuranceInfo?.insuranceNumber ?? '',
      insurerNumber: currentUser?.insuranceInfo?.insurerNumber ?? '',
    });
  }, [currentUser?.insuranceInfo]);

  const [updatePortalUserMutation, { loading }] = useUpdatePortalUserMutation({
    onCompleted: () => {
      notification.success({
        message: formatMessage({ id: 'change-insurance.success' }),
      });
      refetchCurrentUser();
      onClose();
    },
    onError: () => {
      notification.error({
        message: formatMessage({ id: 'change-insurance.error' }),
      });
    },
  });

  const handleSave = () => {
    updatePortalUserMutation({
      variables: {
        input: {
          ...buildPortalUserUpdateInput(currentUser),
          insuranceInfo: {
            insuranceType: insuranceInfo.insuranceType,
            insuranceNumber: insuranceInfo.insuranceNumber || null,
            insurerNumber: insuranceInfo.insurerNumber || null,
          },
        },
      },
    });
  };

  return (
    <Modal
      {...props}
      onCancel={onClose}
      footer={
        <AveliosButton loading={loading} type={'primary'} onClick={handleSave}>
          {formatMessage({ id: 'action.save' })}
        </AveliosButton>
      }
      closable={true}
      title={<h2>{formatMessage({ id: 'change-insurance' })}</h2>}
    >
      <InsuranceInfoForm
        insuranceType={insuranceInfo.insuranceType}
        onInsuranceTypeChange={(value) =>
          setInsuranceInfo((prev) => ({ ...prev, insuranceType: value ?? null }))
        }
        insuranceNumber={insuranceInfo.insuranceNumber}
        onInsuranceNumberChange={(value) =>
          setInsuranceInfo((prev) => ({ ...prev, insuranceNumber: value }))
        }
        insurerNumber={insuranceInfo.insurerNumber}
        onInsurerNumberChange={(value) =>
          setInsuranceInfo((prev) => ({ ...prev, insurerNumber: value }))
        }
      />
    </Modal>
  );
};
